import { useEffect, useState } from 'react';
import { listProviderKeys, type ProviderKeyMeta } from '../../../lib/enrichment/api';
import { ByokConfigDrawer } from './ByokConfigDrawer';

interface MarketplaceProvider {
  key: string;
  name: string;
  dimension: 'firmografia' | 'persona' | 'mobile_phone' | 'verification' | 'web';
  description: string;
  cost: string;
  region: string;
}

const PROVIDERS: MarketplaceProvider[] = [
  {
    key: 'hunter_io',
    name: 'Hunter.io',
    dimension: 'persona',
    description: 'Domain search + email finder. Alternativa a Dropcontact con copertura più ampia (global).',
    cost: 'BYOK',
    region: 'USA',
  },
  {
    key: 'apollo',
    name: 'Apollo.io',
    dimension: 'persona',
    description: '220M+ contatti B2B globali. Database puro → valutare base legale GDPR.',
    cost: 'BYOK',
    region: 'USA',
  },
  {
    key: 'atoka',
    name: 'Atoka (SpazioDati)',
    dimension: 'firmografia',
    description: 'B2B intelligence IT con segnali AI. Premium rispetto a openapi.it.',
    cost: 'BYOK',
    region: 'IT',
  },
  {
    key: 'cribis',
    name: 'Cribis (gruppo CRIF)',
    dimension: 'firmografia',
    description: 'Firmografia + credit risk score. Per studi che fanno credit-check.',
    cost: 'BYOK',
    region: 'IT',
  },
  {
    key: 'builtwith',
    name: 'BuiltWith',
    dimension: 'web',
    description: 'Tech stack del dominio (Shopify, WordPress, Salesforce). Qualifica B2B tech.',
    cost: 'BYOK',
    region: 'Global',
  },
  {
    key: 'zerobounce',
    name: 'ZeroBounce',
    dimension: 'verification',
    description: 'Verifica deliverability email + catch-all + bounce predictor.',
    cost: 'BYOK',
    region: 'Global',
  },
];

export function EnrichmentMarketplaceTab() {
  const [keys, setKeys] = useState<ProviderKeyMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerProvider, setDrawerProvider] = useState<{ key: string; name: string } | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const list = await listProviderKeys();
      setKeys(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const currentByProvider = new Map(keys.map((k) => [k.provider_key, k]));

  return (
    <div className="text-[#37352f]">
      <p className="text-[13px] text-[#787774] mb-4">
        Provider esterni opzionali. Tutti BYOK (Bring Your Own Key): paghi direttamente al provider.
      </p>

      {loading ? (
        <div className="text-[12px] text-[#787774] py-6 text-center">Caricamento provider…</div>
      ) : (
        <div className="divide-y divide-[rgba(55,53,47,0.09)] border border-[rgba(55,53,47,0.09)] rounded-md">
          {PROVIDERS.map((p) => {
            const current = currentByProvider.get(p.key) ?? null;
            const isConfigured = !!current?.has_key;
            const isActive = isConfigured && current!.enabled;
            const statusLabel = !isConfigured ? 'non configurato' : isActive ? 'attivo' : 'in pausa';
            const statusDot = !isConfigured
              ? 'bg-[rgba(55,53,47,0.16)]'
              : isActive
                ? 'bg-[#0f7b6c]'
                : 'bg-[#cb912f]';
            return (
              <div key={p.key} className="flex items-start gap-3 px-3 py-3">
                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${statusDot}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[13px] font-medium">{p.name}</span>
                    <span className="text-[11px] text-[#9b9a97]">· {labelForDim(p.dimension)}</span>
                    <span className="text-[11px] text-[#9b9a97]">· {p.region}</span>
                    <span className="text-[11px] text-[#9b9a97]">· {p.cost}</span>
                    <span className="text-[11px] text-[#787774]">· {statusLabel}</span>
                  </div>
                  <p className="text-[12px] text-[#787774] mt-0.5">{p.description}</p>
                </div>
                <button
                  onClick={() => setDrawerProvider({ key: p.key, name: p.name })}
                  className="text-[12px] text-[#37352f] px-2 py-1 hover:bg-[rgba(55,53,47,0.06)] rounded shrink-0"
                >
                  {isConfigured ? 'Modifica' : 'Configura'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[11px] text-[#9b9a97] mt-4">
        Vuoi suggerire un provider? Scrivici a{' '}
        <a href="mailto:supporto@obly.it" className="underline hover:text-[#37352f]">
          supporto@obly.it
        </a>
      </p>

      {drawerProvider && (
        <ByokConfigDrawer
          providerKey={drawerProvider.key}
          providerName={drawerProvider.name}
          current={currentByProvider.get(drawerProvider.key) ?? null}
          onClose={() => setDrawerProvider(null)}
          onSaved={() => void reload()}
        />
      )}
    </div>
  );
}

function labelForDim(dim: string): string {
  switch (dim) {
    case 'firmografia':
      return 'firmografia';
    case 'persona':
      return 'persona B2B';
    case 'mobile_phone':
      return 'telefono mobile';
    case 'verification':
      return 'verifica email';
    case 'web':
      return 'web signals';
    default:
      return dim;
  }
}
